import React, {useState} from 'react';
import {
    Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
    DrawerFooter, DrawerHeader, DrawerOverlay, FormLabel, useToast,
} from "@chakra-ui/react";
import {Form} from "antd";
import {Input} from "@nextui-org/react";
import {
    getAuth, EmailAuthProvider, reauthenticateWithCredential, updatePassword, signOut,
} from "firebase/auth";
import Firebase from "../../Firebase/Firebase";
import {useNavigate} from "react-router-dom";

type Props = {
    changePassword: boolean;
    closePassword:  (val: boolean) => void;
};

const firebaseAuth = getAuth(Firebase);

const FRIENDLY: Record<string, string> = {
    'auth/wrong-password':        'Current password is incorrect.',
    'auth/invalid-credential':    'Current password is incorrect.',
    'auth/user-mismatch':         'Email does not match your account.',
    'auth/too-many-requests':     'Too many attempts. Please try again later.',
    'auth/weak-password':         'New password must be at least 6 characters.',
    'auth/requires-recent-login': 'Session expired. Please log out and log back in before changing your password.',
};

const ChangePassword: React.FC<Props> = ({changePassword, closePassword}) => {
    const navigate  = useNavigate();
    const toast     = useToast();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (values: any) => {
        const user = firebaseAuth.currentUser;
        if (!user) {
            toast({position:'top', title:'Not signed in', status:'error', duration:3000, isClosable:true});
            return;
        }

        setLoading(true);
        try {
            // Re-authenticate before sensitive change
            const credential = EmailAuthProvider.credential(values.email, values.currentPassword);
            await reauthenticateWithCredential(user, credential);

            await updatePassword(user, values.newPassword);

            toast({
                position: 'top',
                title: 'Password changed',
                description: 'Your password has been updated. Please log in again.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Clear 2FA session so the next login requires fresh OTP
            sessionStorage.removeItem(`otp_verified_${user.uid}`);

            await signOut(firebaseAuth);
            navigate('/');
        } catch (e: any) {
            const msg = FRIENDLY[e?.code] ?? 'Failed to change password. Please try again.';
            toast({position:'top', title:'Error', description: msg, status:'error', duration:4000, isClosable:true});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer isOpen={changePassword} placement="right" onClose={() => closePassword(false)}>
            <DrawerOverlay/>
            <DrawerContent>
                <DrawerCloseButton/>
                <DrawerHeader borderBottomWidth="1px">Change Password</DrawerHeader>

                <DrawerBody>
                    <Form onFinish={handleSubmit} id="passwordForm" layout="vertical" className="mt-2">
                        <FormLabel htmlFor="email" className="ml-1">Your email</FormLabel>
                        <Form.Item name="email"
                            rules={[{required:true, message:'Required'}, {type:'email', message:'Invalid email'}]}>
                            <Input type="email" size="lg" width="100%" onChange={() => {}}
                                   placeholder="Your email address" className="my-1"/>
                        </Form.Item>

                        <FormLabel htmlFor="currentPassword" className="ml-1">Current password</FormLabel>
                        <Form.Item name="currentPassword" rules={[{required:true, message:'Required'}]}>
                            <Input.Password size="lg" width="100%" onChange={() => {}}
                                           placeholder="Your current password" className="my-1"/>
                        </Form.Item>

                        <FormLabel htmlFor="newPassword" className="ml-1">New password</FormLabel>
                        <Form.Item name="newPassword"
                            rules={[
                                {required:true, message:'Required'},
                                {min:8, message:'Password must be at least 8 characters'},
                            ]}>
                            <Input.Password size="lg" width="100%" onChange={() => {}}
                                           placeholder="New password (min 8 characters)" className="my-1"/>
                        </Form.Item>

                        <FormLabel htmlFor="confirmPassword" className="ml-1">Confirm new password</FormLabel>
                        <Form.Item name="confirmPassword"
                            rules={[
                                {required:true, message:'Required'},
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('newPassword') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject('Passwords do not match.');
                                    },
                                }),
                            ]}>
                            <Input.Password size="lg" width="100%" onChange={() => {}}
                                           placeholder="Confirm new password" className="my-1"/>
                        </Form.Item>
                    </Form>
                </DrawerBody>

                <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={() => closePassword(false)}>Cancel</Button>
                    <Button type="submit" form="passwordForm" isLoading={loading} colorScheme="blue">
                        Change Password
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export default ChangePassword;
